/**
 * `102 {…}` / `102 }` nesting-depth bookkeeping for a flat DXF group walk.
 *
 * AutoCAD wraps persistent reactors and the extension dictionary of a record
 * in balanced `102 {ACAD_REACTORS … 102 }` / `102 {ACAD_XDICTIONARY … 102 }`
 * blocks (Autodesk DXF reference "Common Group Codes for Objects"). Every group
 * INSIDE such a block — including an inner `330` / `360` / `5` — belongs to the
 * nested structure, never to the record itself, so a reader looking for a
 * record-level field (the `330` owner, the own `5`, a `1` name) must skip them.
 * This is the SINGLE definition of that rule (T9b review P3), shared by the
 * dxf-io table parsers and the drawing-model bridge helpers.
 */
import type { DxfGroup } from "./types";

/**
 * Advance the nesting depth for one group: a `102` whose value starts with `{`
 * opens a block, a bare `}` closes it (never below zero); any other group
 * leaves the depth unchanged. Callers skip a group when the returned depth is
 * > 0 OR the group itself is a `102` marker.
 */
export function trackNestedBlockDepth(g: DxfGroup, depth: number): number {
  if (g.code !== 102) return depth;
  const tok = g.value.trim();
  if (tok.startsWith("{")) return depth + 1;
  if (tok === "}") return depth > 0 ? depth - 1 : 0;
  return depth;
}

/**
 * `true` when `g` is part of a record's TOP-LEVEL payload, i.e. neither a
 * `102` boundary marker nor a group inside an open `102 {…}` block. `depth` is
 * the value {@link trackNestedBlockDepth} returned for THIS group.
 */
export function isTopLevelGroup(g: DxfGroup, depth: number): boolean {
  return g.code !== 102 && depth === 0;
}
